// eslint-disable-next-line no-restricted-imports
import { useEffect, useMemo, useRef, useState } from 'react';
import { Redirect } from 'react-router-dom';
import {
  Button,
  Flex,
  GridAlpha,
  Heading,
  Select,
  Spinner,
  Table,
  Tag,
  Text,
  TextInput,
  useToasts,
} from '@applyboard/crystal-ui';
import { useEventTracker, useTranslation } from '@applyboard/ui-utils';
import { refetchType } from '../../../data';
import { useGetLoanTemplates } from '../../../data/loanTemplates';
import {
  Document,
  FileType,
  LoanApplicationV2Response,
  LoansError,
  SetLoanTemplateReqBody,
  deleteFile,
  fetchSupportingDocument,
  useGetLoan,
  useGetLoanDocuments,
  useSendFollowUpEmail,
  useSendSubmissionEmail,
  useSetLoanTemplate,
  useUpdateDocuments,
} from '../../../data/loans';
import {
  DocumentStatusMetadata,
  DocumentStatusMetadataType,
  LoansFollowupFormEvents,
  StatusMetadata,
} from '../constants';
import { DocumentFile } from './DocumentFile';

export interface Template {
  id: string;
  name: string;
  fields: Array<Field>;
}

export interface Field {
  type: string;
  value: string;
  label: string;
}

type LoansFollowupFormProps = {
  match: {
    params: {
      loanId?: string;
    };
  };
};

function LoansFollowupForm({
  match: {
    params: { loanId },
  },
}: LoansFollowupFormProps) {
  const loanApplicationId = useRef(loanId);
  const { loanTemplates, isFetchingLoanTemplates } = useGetLoanTemplates();
  const { loan, isFetchingLoan, refetchLoan } = useGetLoan(loanApplicationId.current ?? '');
  const templateMap =
    loanTemplates?.reduce(
      (acc, template) => {
        acc[template.id] = template;
        return acc;
      },
      {} as Record<string, Template>,
    ) ?? {};
  const { documents, loanDocumentsError, refetchLoanDocuments, isFetchedLoanDocuments } =
    useGetLoanDocuments(loanApplicationId.current ?? '');

  if (loanDocumentsError) {
    return <Redirect to="/500" />;
  }

  if (isFetchingLoanTemplates || isFetchingLoan || !isFetchedLoanDocuments) {
    return (
      <Flex align="center" justify="center">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (!templateMap || Object.entries(templateMap).length < 1 || !loan) {
    return <Redirect to="/500" />;
  }

  return (
    <DataSectionComponent
      loan={loan}
      templateMap={templateMap}
      refetchLoan={refetchLoan}
      documents={documents ?? []}
      refetchLoanDocuments={refetchLoanDocuments}
    />
  );
}

type DataSectionComponentProps = {
  loan?: LoanApplicationV2Response;
  refetchLoan: refetchType;
  templateMap: Record<string, Template>;
  documents: Array<Document>;
  refetchLoanDocuments: refetchType;
};

function DataSectionComponent({
  loan,
  templateMap,
  refetchLoan,
  documents,
  refetchLoanDocuments,
}: DataSectionComponentProps) {
  const { t } = useTranslation();
  const { trackEvent } = useEventTracker();
  const toasts = useToasts();
  const loanId = loan?.data.id;
  const { setLoanTemplate } = useSetLoanTemplate(loanId ?? '');
  const [templateButtonDisabled, setTemplateButtonDisabled] = useState(false);
  const currentTemplateId = loan?.data.attributes?.follow_up_template_id ?? '';
  const currentTemplate = templateMap[currentTemplateId] ?? null;
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(currentTemplate);
  const [sendFollowUpEmail, isFollowUpEmailSending] = useSendFollowUpEmail();
  const { updateDocuments } = useUpdateDocuments(loanId ?? '');
  const [bankEmail, setBankEmail] = useState('');
  const { sendSubmissionEmail, isSendSubmissionEmailLoading } = useSendSubmissionEmail();

  useEffect(() => {
    trackEvent({
      event: LoansFollowupFormEvents.AdminPageView,
      data: { loan_id: loanId },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mappedData = useMemo(
    () => mapDocumentsWithSingleTemplate(documents, DocumentStatusMetadata, selectedTemplate),
    [documents, selectedTemplate],
  );

  const handleDocumentStatusUpdate = (status: string, documentType: string) => {
    const requestBody = {
      documents: [
        {
          type: documentType,
          status: status,
        },
      ],
    };

    updateDocuments(
      requestBody,
      data => {
        console.log('Document state updated successfully:', data);
        if (status === 'VERIFIED') {
          trackEvent({
            event: LoansFollowupFormEvents.VerifyDocument,
            data: { loan_id: loanId, document_type: documentType },
          });
        } else if (status === 'REJECTED') {
          trackEvent({
            event: LoansFollowupFormEvents.RejectDocument,
            data: { loan_id: loanId, document_type: documentType },
          });
        }
        refetchLoanDocuments();
        toasts.positive('Document state updated successfully!');
      },
      error => console.error('Error updating document state:', error),
    );
  };

  const handleSendFollowUpEmailClick = async (id: string) => {
    try {
      await sendFollowUpEmail(id);
      toasts.positive('Email sent successfully!');
      trackEvent({
        event: LoansFollowupFormEvents.EmailStudent,
        data: { loan_id: loanId },
      });
    } catch (error) {
      let errorMessage = t('common.errors.submission');
      if (error instanceof LoansError) {
        const loansError = error as LoansError;
        errorMessage =
          loansError.errorBody.message instanceof Array
            ? loansError.errorBody.message.join(',')
            : loansError.errorBody.message;

        toasts.negative(new Error(errorMessage));
      } else {
        toasts.negative(new Error('Failed to send follow up to student'));
      }
    }
  };

  const setTemplateCallback = () => {
    setTemplateButtonDisabled(true);
    setLoanTemplate(
      {
        follow_up_template_id: selectedTemplate?.id,
      } as SetLoanTemplateReqBody,
      async () => {
        setTemplateButtonDisabled(false);
        toasts.positive(
          t('loans.followup.manageTemplate.setTemplateSuccess', {
            applicationId: loanId,
          }),
        );
        trackEvent({
          event: LoansFollowupFormEvents.SetTemplate,
          data: { loan_id: loanId, template_id: selectedTemplate?.id },
        });
        await refetchLoan();
      },
      error => {
        setTemplateButtonDisabled(false);
        console.error(error);
        toasts.negative(error as Error);
      },
    );
  };

  const handleFetchDocumentClick = async (
    externalId: string,
    documentType: string,
    fileName: string,
  ) => {
    try {
      const response = await fetchSupportingDocument(externalId, documentType, fileName);
      window.open(response.data.attributes.presigned_url, '_blank');
    } catch (error) {
      if (error instanceof LoansError) {
        let errorMessage = t('common.errors.submission');
        const loansError = error as LoansError;
        console.log(loansError.errorBody);
        errorMessage =
          loansError.errorBody.message instanceof Array
            ? loansError.errorBody.message.join(',')
            : loansError.errorBody.message;

        toasts.negative(new Error(errorMessage));
      } else {
        toasts.negative(new Error('Failed to fetch document'));
      }
    }
  };

  const handleSendSubmissionEmail = (bankEmail: string) => {
    sendSubmissionEmail(
      loanId ?? '',
      { bank_email: bankEmail },
      () => {
        toasts.positive('Email sent successfully!');
        trackEvent({
          event: LoansFollowupFormEvents.EmailLoanProvider,
          data: { loan_id: loanId },
        });
      },
      error => {
        toasts.negative(error as Error);
        if (error instanceof Error) {
          console.error(`Failed to send email: ${error.message}`);
        } else {
          console.error('Failed to send email:', error);
        }
      },
    );
  };

  const handleDeleteFileClick = async (
    externalId: string,
    documentType: string,
    fileName: string,
  ) => {
    try {
      const result = confirm(t('loans.followup.deleteFile.confirm'));
      if (!result) {
        return;
      }
      await deleteFile(externalId, documentType, fileName);
      toasts.positive(t('loans.followup.deleteFile.success'));
      trackEvent({
        event: LoansFollowupFormEvents.DeleteFile,
        data: { loan_id: loanId, document_type: documentType },
      });

      // Might be better if the delete call returned the loan application so an extra request isn't needed
      await refetchLoanDocuments();
    } catch (error) {
      if (error instanceof LoansError) {
        let errorMessage = t('common.errors.submission');
        const loansError = error as LoansError;
        console.log(loansError.errorBody);
        errorMessage =
          loansError.errorBody.message instanceof Array
            ? loansError.errorBody.message.join(',')
            : loansError.errorBody.message;

        toasts.negative(new Error(errorMessage));
      } else {
        toasts.negative(new Error(t('loans.followup.deleteFile.error')));
      }
    }
  };

  return (
    <GridAlpha columns="repeat(27, 1fr)" rowGap={6} pb={6}>
      <GridAlpha.Item columnStart={2} columnEnd={11}>
        <Heading level={1} align="start" intent="secondary" variant="titleL" contrast="high">
          {t('loans.followup.title')}
        </Heading>
      </GridAlpha.Item>
      <GridAlpha.Item columnStart={2} columnEnd={11}>
        <Heading level={2} align="start">
          {t('loans.followup.selectTemplate.heading')}
        </Heading>
        <Select
          appearance="native"
          helpText={t('loans.followup.selectTemplate.helpText')}
          aria-label={t('loans.followup.selectTemplate.helpText')}
          value={selectedTemplate?.id ?? currentTemplate?.id}
          onChange={(value: string) => {
            setSelectedTemplate(templateMap[value] ?? null);
          }}
        >
          {Object.values(templateMap).map(opt => (
            <Select.Option key={opt.id} label={opt.name} value={opt.id} />
          ))}
        </Select>
      </GridAlpha.Item>
      <GridAlpha.Item columnStart={13} columnEnd={21}>
        <Heading level={2} align="start">
          {t('loans.followup.manageTemplate.heading')}
        </Heading>
        <Flex align="start" justify="start" direction="column" gap={2}>
          <Text>{`Application is using template: ${currentTemplate?.name ?? 'None'}`}</Text>
          <Button
            id="set-template-for-application"
            onClick={setTemplateCallback}
            size="sm"
            disabled={templateButtonDisabled}
            emphasis="contained"
          >
            {t('loans.followup.manageTemplate.setTemplate')}
          </Button>
          <Button
            id="email-student"
            onClick={() => {
              handleSendFollowUpEmailClick(loanId ?? '');
            }}
            size="sm"
            emphasis="contained"
            disabled={isFollowUpEmailSending}
          >
            {t('loans.followup.manageTemplate.emailStudent')}
          </Button>
          <TextInput
            aria-describedby=""
            aria-label=""
            helpText=""
            intent="secondary"
            label="Bank Email"
            onChange={function Pa(e) {
              setBankEmail(e);
            }}
            placeholder=""
            size="md"
            type="text"
            value={bankEmail}
          />
          <Button
            id="send-to-loan-provider"
            onClick={() => {
              handleSendSubmissionEmail(bankEmail);
            }}
            size="sm"
            emphasis="contained"
            disabled={isSendSubmissionEmailLoading}
          >
            {t('loans.followup.manageTemplate.sendToLoanProvider')}
          </Button>
        </Flex>
      </GridAlpha.Item>
      <GridAlpha.Item columnStart={2} columnEnd={27}>
        <Heading level={2} align="start">
          {t('loans.followup.viewTemplate.heading')}
        </Heading>
        <Table
          columns={[
            {
              accessorKey: 'label',
              header: t('loans.followup.viewTemplate.fieldName'),
              cell: (props: MappedDocumentData) => {
                return (
                  <Table.Cell>
                    <Flex wrap>{props.label}</Flex>
                  </Table.Cell>
                );
              },
            },
            {
              accessorKey: 'state',
              header: t('loans.followup.viewTemplate.state'),
              cell: (props: MappedDocumentData) => {
                return (
                  <Table.Cell>
                    {/* This is not ideal. We're doing this to show that documents is still loading.
                    This is primarily done to avoid table refreshes on document status updates.
                    Ideally this should be in the mapper as a state. */}
                    {documents.length < 0 ? (
                      <Flex align="center" justify="center">
                        <Spinner size="sm" />
                      </Flex>
                    ) : (
                      <Tag intent={props.metadata?.color ?? 'primary'} size="sm" key="key">
                        {props.metadata?.label}
                      </Tag>
                    )}
                  </Table.Cell>
                );
              },
            },
            {
              accessorKey: 'actions',
              header: t('loans.followup.viewTemplate.actions'),
              cell: (props: MappedDocumentData) => (
                <Table.Cell>
                  <Flex align="center" justify="start" direction="row" gap={2}>
                    <Button
                      id="verify"
                      size="sm"
                      emphasis="highlighted"
                      onClick={() => {
                        handleDocumentStatusUpdate('VERIFIED', props.value);
                      }}
                    >
                      {t('loans.followup.viewTemplate.verify')}
                    </Button>
                    <Button
                      id="reject"
                      size="sm"
                      emphasis="highlighted"
                      onClick={() => {
                        handleDocumentStatusUpdate('REJECTED', props.value);
                      }}
                    >
                      {t('loans.followup.viewTemplate.reject')}
                    </Button>
                  </Flex>
                </Table.Cell>
              ),
            },
            {
              accessorKey: 'submissions',
              header: t('loans.followup.viewTemplate.studentSubmissions'),
              cell: (props: MappedDocumentData) => {
                const files = props.files as FileType[];

                return (
                  <Table.Cell>
                    <Flex direction="column" align="start" gap={6} wrap>
                      {files.map((file, index) => (
                        <DocumentFile
                          key={index}
                          index={index}
                          loanId={loanId ?? ''}
                          props={props}
                          file={file}
                          handleFetchDocumentClick={handleFetchDocumentClick}
                          handleDeleteFileClick={handleDeleteFileClick}
                        />
                      ))}
                    </Flex>
                  </Table.Cell>
                );
              },
            },
          ]}
          data={mappedData}
          overflow="scroll"
        />
      </GridAlpha.Item>
    </GridAlpha>
  );
}

export type MappedDocumentData = {
  type: string;
  value: string;
  label: string;
  status?: string;
  metadata?: StatusMetadata;
  files: FileType[];
  templateFields: Array<Field>;
};
export function mapDocumentsWithSingleTemplate(
  documents: Document[],
  statusMetadata: DocumentStatusMetadataType,
  template: Template | null,
): Array<MappedDocumentData> {
  const documentMap: Record<string, Document> = documents.reduce(
    (acc, doc) => {
      acc[doc.type.trim().toLowerCase()] = doc;
      return acc;
    },
    {} as Record<string, Document>,
  );

  // Map template fields, merging document data if available
  return (
    template?.fields?.map(field => {
      const doc = documentMap[field.value.trim().toLowerCase()];
      const metadata = statusMetadata[doc?.status as keyof typeof DocumentStatusMetadata];
      return {
        type: field.type,
        value: field.value,
        label: field.label,
        status: doc?.status ?? undefined,
        metadata,
        files: doc?.files ?? [],
        templateFields: [field],
      };
    }) ?? []
  );
}

export default LoansFollowupForm;
